import CrossIcon from '@atlaskit/icon/glyph/cross';
import MenuIcon from '@atlaskit/icon/glyph/menu';
import Tooltip from '@atlaskit/tooltip';
import { breakpointValues, useResponsiveBreakpoint } from '@atlassian/mpac-primitives';
import React, { useEffect } from 'react';

import { isBranchDeployment } from '../../../../common/utils/branchDeploy';
import { BranchDeploymentIndicator } from './BranchDeploymentIndicator';
import { MenuBackButton, Search, SignIn } from './Buttons';
import { HelpDropdown, UserDropdown } from './Dropdown';
import {
  ExpandableMenuDesktop,
  expandableMenuLabel,
  ExpandableMenuMobile,
  ExpandableMenuType,
  useExpandableMenuState,
} from './ExpandableMenu';
import { getLinksFromUserResponse } from './getLinksFromUserResponse';
import {
  ActionButton,
  ActionButtonsContainer,
  Container,
  LogoAndMenuContainer,
  MenuButtonsContainer,
  NavbarBackground,
  NavbarContainer,
  VerticalRule,
} from './GlobalNav.styles';
import { MarketplaceLogo } from './MarketplaceLogo';
import { PartnerQuicklinks } from './PartnerQuicklinks';
import { useDesktopKeyboardNavigation } from './useDesktopKeyboardNavigation';

import type { GlobalNavInnerProps, GlobalNavProps } from './types';
import type { UserProps } from './userTypes';

export const DesktopGlobalNav = ({
  openMenu,
  closeMenu,
  isMenuOpen,
  openedMenu,
  history,
  showSearchIcon = true,
  userProps,
}: GlobalNavInnerProps) => {
  const { expandableMenuRef } = useDesktopKeyboardNavigation({
    openedMenu,
    isMenuOpen,
    closeMenu,
  });

  return (
    <div>
      <NavbarBackground>
        <NavbarContainer>
          <LogoAndMenuContainer>
            {isBranchDeployment() && <BranchDeploymentIndicator />}
            <MarketplaceLogo history={history} closeMenu={closeMenu} />
            <MenuButtonsContainer role="navigation" aria-label="Primary">
              <ExpandableMenuDesktop
                ref={expandableMenuRef}
                openMenu={openMenu}
                userProps={userProps}
                isOpen={isMenuOpen}
                openedMenu={openedMenu}
                closeMenu={closeMenu}
              />
            </MenuButtonsContainer>
          </LogoAndMenuContainer>
          <ActionButtonsContainer role="navigation" aria-label="Secondary">
            {showSearchIcon && <Search />}
            <HelpDropdown />
            <VerticalRule />
            {userProps.user ? (
              <UserDropdown userProps={userProps} />
            ) : (
              <SignIn userProps={userProps} />
            )}
          </ActionButtonsContainer>
        </NavbarContainer>
      </NavbarBackground>
    </div>
  );
};

export const MobileGlobalNav = ({
  openMenu,
  closeMenu,
  isMenuOpen,
  openedMenu,
  history,
  showSearchIcon = true,
  userProps,
}: GlobalNavInnerProps) => {
  const showBackButton = isMenuOpen && openedMenu && openedMenu !== ExpandableMenuType.MOBILE_VIEW;

  return (
    <Container>
      <NavbarBackground>
        <NavbarContainer>
          {showBackButton ? (
            <ActionButtonsContainer>
              <MenuBackButton
                onClick={openMenu(ExpandableMenuType.MOBILE_VIEW)}
                label={expandableMenuLabel[openedMenu]}
              />
            </ActionButtonsContainer>
          ) : (
            <LogoAndMenuContainer>
              {isBranchDeployment() && <BranchDeploymentIndicator />}
              <MarketplaceLogo history={history} closeMenu={closeMenu} />
            </LogoAndMenuContainer>
          )}
          <ActionButtonsContainer>
            {!showBackButton && showSearchIcon && <Search />}
            {isMenuOpen ? (
              <Tooltip content="Back to Navigation">
                <ActionButton onClick={closeMenu} tabIndex={0}>
                  <CrossIcon label="Close" size="large" />
                </ActionButton>
              </Tooltip>
            ) : (
              <ActionButton onClick={openMenu(ExpandableMenuType.MOBILE_VIEW)} tabIndex={0}>
                <MenuIcon label="Menu" size="large" />
              </ActionButton>
            )}
          </ActionButtonsContainer>
        </NavbarContainer>
      </NavbarBackground>
      <ExpandableMenuMobile
        isOpen={isMenuOpen}
        openedMenu={openedMenu}
        openMenu={openMenu}
        userProps={userProps}
        closeMenu={closeMenu}
      />
    </Container>
  );
};

export const GlobalNav = ({
  history,
  userResponse,
  onSwitchAccount,
  onLogout,
  showSearchIcon = true,
}: GlobalNavProps) => {
  const { openMenu, closeMenu, isMenuOpen, openedMenu } = useExpandableMenuState();
  const { value: breakpoint } = useResponsiveBreakpoint();
  const isMobile = breakpoint < breakpointValues.md;

  const userProps: UserProps = {
    isUserLoading: Boolean(userResponse.loading),
    userLinks: getLinksFromUserResponse(userResponse.data),
    user: userResponse.data?._embedded?.user,
    onSwitchAccount,
    onLogout,
  };
  const { lastVisitedPartnerName, lastVisitedPartnerId } =
    userResponse.data?._embedded?.lastVisited || {};

  useEffect(() => {
    /* Close the menu when switching from mobile to desktop view if ExpandableMenuType.MOBILE_VIEW is open, as it's intended only for mobile use. */
    if (!isMobile && isMenuOpen && openedMenu === ExpandableMenuType.MOBILE_VIEW) {
      closeMenu();
    }
  }, [isMobile, openedMenu, isMenuOpen]);

  return (
    <Container aria-label="Header">
      {lastVisitedPartnerName && lastVisitedPartnerId && (
        <PartnerQuicklinks
          lastVisitedPartnerName={lastVisitedPartnerName}
          lastVisitedPartnerId={lastVisitedPartnerId}
        />
      )}
      {isMobile ? (
        <MobileGlobalNav
          openMenu={openMenu}
          closeMenu={closeMenu}
          isMenuOpen={isMenuOpen}
          openedMenu={openedMenu}
          history={history}
          userProps={userProps}
          showSearchIcon={showSearchIcon}
        />
      ) : (
        <DesktopGlobalNav
          openMenu={openMenu}
          closeMenu={closeMenu}
          isMenuOpen={isMenuOpen}
          openedMenu={openedMenu}
          history={history}
          showSearchIcon={showSearchIcon}
          userProps={userProps}
        />
      )}
    </Container>
  );
};
